import { useState, useRef, useEffect } from 'react';

export interface RowActionsMenuItem {
  /** Stable identity for the item — used as the React key. */
  key: string;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  /** Native tooltip — typically the reason an item is disabled (e.g. "Orders coming soon"). */
  title?: string;
  /** Trailing badge text rendered after the label (e.g. "Soon"). */
  hint?: string;
}

interface RowActionsMenuProps {
  /** Full accessible name for the trigger + menu (e.g. "More actions for Acme Pty Ltd"). */
  label: string;
  items: RowActionsMenuItem[];
  /** Disables the trigger button entirely (e.g. while the caller's role check is loading). */
  disabled?: boolean;
}

/**
 * Overflow ("…") button + dropdown for per-row quick actions that are secondary or
 * gated (#13136 — promoted from `apps/admin-portal/src/pages/accounts/RowActionsMenu.tsx`,
 * generalised from a hardcoded account "+Order" item to an arbitrary `items` list).
 *
 * Accessible: trigger has `aria-haspopup="menu"` + `aria-expanded`; closes on outside
 * click and Escape (returning focus to the trigger). Menu items use `role="menuitem"`.
 *
 * #13185 — single-overflow, no-wrap by construction: the trigger is one fixed-size
 * (`w-7 h-7`) button rendering a single short glyph, so it can never itself wrap
 * across lines; `shrink-0` defends the wrapper against being squeezed to zero width
 * when it sits as the last cell in a narrow/flex table-actions column.
 */
export function RowActionsMenu({ label, items, disabled = false }: RowActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape and return focus to trigger
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const handleItemClick = (item: RowActionsMenuItem) => {
    if (item.disabled) return;
    item.onClick?.();
    setOpen(false);
  };

  return (
    <div className="relative inline-block shrink-0 whitespace-nowrap" ref={menuRef}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center justify-center w-7 h-7 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-500/50 text-xs font-medium"
      >
        •••
      </button>

      {open && (
        <div
          role="menu"
          aria-label={label}
          className="absolute right-0 z-20 mt-1 w-44 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none"
        >
          {items.map(item => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              aria-disabled={item.disabled ? 'true' : undefined}
              title={item.title}
              onClick={() => handleItemClick(item)}
              className={
                item.disabled
                  ? 'flex w-full items-center px-4 py-2 text-sm text-gray-400 cursor-not-allowed select-none'
                  : 'flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50'
              }
            >
              {item.label}
              {item.hint && <span className="ml-auto text-xs text-gray-300">{item.hint}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
